import * as React from "react";
import { toast, Toaster, type ExternalToast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * BrandToast Component
 *
 * EverReal branded toast notifications with:
 * - Success: Emerald + gold accent
 * - Error: Red + gold accent
 * - Warning: Amber + emerald text
 * - Info: Blue + gold accent
 */

/**
 * Toast Provider Component
 * Renders the toast container with brand styling
 */
export interface BrandToastProviderProps {
  /**
   * Toast position
   */
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "top-center"
    | "bottom-center";

  /**
   * Maximum number of toasts to show
   */
  visibleToasts?: number;

  /**
   * Expand toasts by default
   */
  expand?: boolean;

  /**
   * Theme
   */
  theme?: "light" | "dark" | "system";
}

export function BrandToastProvider({
  position = "top-right",
  visibleToasts = 5,
  expand = false,
  theme = "dark",
}: BrandToastProviderProps) {
  return (
    <Toaster
      position={position}
      visibleToasts={visibleToasts}
      expand={expand}
      theme={theme}
      closeButton
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: cn(
            // Base styles
            "w-full rounded-md border-2 p-4 shadow-gold-lg backdrop-blur-sm",
            "flex items-start gap-3",
            "animate-in slide-in-from-top-2 fade-in-0 duration-300",
            "data-[closed]:animate-out data-[closed]:slide-out-to-right-full data-[closed]:fade-out-0",
          ),
          title: "text-sm font-semibold",
          description: "text-sm opacity-90",
          actionButton: cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
            // The toast container is a flex row; without these the button
            // gets squeezed by long titles (e.g. the "Market submitted —
            // finalising on-chain" pending toast) until "View" wraps one
            // letter per line. `!` modifier wins against Sonner's
            // `[data-sonner-toast] [data-button]` selector (specificity
            // 0,1,1) which beats a plain Tailwind utility (0,1,0); we lost
            // that battle without `!important` and the button rendered as
            // a vertical letter column.
            "!shrink-0 !whitespace-nowrap min-w-fit",
            "bg-brand-accent text-white",
            "hover:bg-amber-600",
          ),
          cancelButton: cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
            "!shrink-0 !whitespace-nowrap min-w-fit",
            "bg-slate-100 text-text-secondary",
            "hover:bg-slate-200",
          ),
          closeButton: cn(
            "!absolute !right-2 !top-2",
            "!w-6 !h-6 rounded-full transition-colors",
            "!bg-current/10 hover:!bg-current/20",
            "!text-current !opacity-70 hover:!opacity-100",
            "flex items-center justify-center",
            "!border-0 !p-0",
          ),
        },
      }}
    />
  );
}

/**
 * Toast Icons
 */
const SuccessIcon = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-green-600"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </div>
);

const ErrorIcon = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-red-400"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  </div>
);

const WarningIcon = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-amber-400"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  </div>
);

const InfoIcon = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-blue-400"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  </div>
);

const LoadingIcon = () => (
  <div className="flex-shrink-0 w-5 h-5">
    <svg
      className="animate-spin text-brand-primary"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

/**
 * Custom toast functions with brand styling
 */

interface BrandToastOptions extends ExternalToast {
  icon?: React.ReactNode;
}

// Stable id derived from message+description so rapid duplicate calls collapse
// into one toast (sonner dedupes by id). Caller can override by passing `id`.
function dedupeId(variant: string, message: string, description?: unknown) {
  const desc = typeof description === "string" ? description : "";
  return `${variant}:${message}::${desc}`;
}

export const brandToast = {
  /**
   * Success toast
   */
  success: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      id: dedupeId("success", message, options?.description),
      ...options,
      icon: options?.icon || <SuccessIcon />,
      className: cn(
        "bg-green-50 border-green-200",
        "text-green-800",
        options?.className,
      ),
    });
  },

  /**
   * Error toast
   */
  error: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      id: dedupeId("error", message, options?.description),
      ...options,
      icon: options?.icon || <ErrorIcon />,
      className: cn(
        "bg-red-900/90 border-red-500/50",
        "text-white",
        options?.className,
      ),
    });
  },

  /**
   * Warning toast
   */
  warning: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      id: dedupeId("warning", message, options?.description),
      ...options,
      icon: options?.icon || <WarningIcon />,
      className: cn(
        "bg-amber-50 border-amber-200",
        "text-amber-800",
        options?.className,
      ),
    });
  },

  /**
   * Info toast
   */
  info: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      id: dedupeId("info", message, options?.description),
      ...options,
      icon: options?.icon || <InfoIcon />,
      className: cn(
        "bg-blue-50 border-blue-200",
        "text-brand-primary",
        options?.className,
      ),
    });
  },

  /**
   * Loading toast.
   *
   * Defaults to `duration: Infinity` because "loading" usually means
   * "show until I dismiss you" (caller holds the returned id and calls
   * `dismiss(id)` on completion). But callers like `transactionToast.pending`
   * have a different lifetime model — the tx is in flight, not under the
   * caller's direct control — and want auto-dismiss. So we respect an
   * explicit `duration` override; only fall back to Infinity when absent.
   */
  loading: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      ...options,
      icon: options?.icon || <LoadingIcon />,
      className: cn(
        "bg-white border-border-card",
        "text-text-primary",
        options?.className,
      ),
      duration: options?.duration ?? Infinity,
    });
  },

  /**
   * Promise toast - shows loading, then success/error
   */
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
    options?: BrandToastOptions,
  ) => {
    return toast.promise(promise, {
      loading: msgs.loading,
      success: msgs.success,
      error: msgs.error,
      ...options,
    });
  },

  /**
   * Custom toast with full control
   */
  custom: (message: string, options?: BrandToastOptions) => {
    return toast(message, {
      id: dedupeId("custom", message, options?.description),
      ...options,
      className: cn(
        "bg-white border-border-card",
        "text-text-primary",
        options?.className,
      ),
    });
  },

  /**
   * Dismiss a toast
   */
  dismiss: (toastId?: string | number) => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: () => {
    toast.dismiss();
  },
};

/**
 * Transaction Toast - specialized for blockchain transactions
 */
export interface TransactionToastOptions extends BrandToastOptions {
  txHash?: string;
  explorerUrl?: string;
}

export const transactionToast = {
  /**
   * Transaction pending.
   *
   * Auto-dismisses after 8s by default. Reasoning: this toast is shown when
   * the backend relay's ~5s confirmation poll timed out but the tx is still
   * broadcast — the Helius webhook will index it within seconds, and the
   * page navigates to "/" where the market shows up in the feed. Once the
   * user is looking at the feed, the toast is no longer load-bearing
   * information (the toast itself says "It'll appear in the list shortly").
   * The previous behavior inherited `duration: Infinity` from `loading()`
   * and required a manual close-click.
   */
  pending: (message: string, options?: TransactionToastOptions) => {
    return brandToast.loading(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
      duration: options?.duration ?? 8000,
    });
  },

  /**
   * Transaction success
   */
  success: (message: string, options?: TransactionToastOptions) => {
    return brandToast.success(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
    });
  },

  /**
   * Transaction failed
   */
  error: (message: string, options?: TransactionToastOptions) => {
    return brandToast.error(message, {
      ...options,
      description: options?.txHash
        ? `Transaction: ${options.txHash.slice(0, 6)}...${options.txHash.slice(-4)}`
        : undefined,
      action: options?.explorerUrl
        ? {
            label: "View",
            onClick: () => window.open(options.explorerUrl, "_blank"),
          }
        : undefined,
    });
  },
};
